// React
import React, { useState, useEffect, useRef } from "react";

// External libraries
import {
    closestCenter,
    DndContext,
    MouseSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    restrictToParentElement,
    restrictToVerticalAxis
} from "@dnd-kit/modifiers";
import {
    SortableContext
} from "@dnd-kit/sortable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinnerThird, faSearch, faRefresh, faCheckSquare, faSquare, faTableCells, faList, faSort, faSortAmountDown, faSortAmountUp, faFilter, faSidebarFlip } from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { Button, ButtonMenuDropDirection } from "../buttons/button"
import { getDataTestId } from "../functions/data-test-ids";
import { isNullOrUndefined } from "../functions/general";
import { useOverflowObserver } from "../hooks/useOverflowObserver.tsx";
import { InputString } from "../inputs/input-string.jsx";
import { ButtonToggle } from "../buttons/button-toggle.jsx";
import { SELECT_COLUMNS_NONE_SENTINEL, SelectTableColumnsDropdown } from "./select-table-columns-dropdown";
import { TableCard } from "./table-card";
import { TableFieldType, TableSortDirection, TableViewMode } from "./table-constants";
import { TableHeaderRow } from "./table-header";
import { TableRow } from "./table-row";
import { useColumnResize } from "./useColumnResize";

// CSS
import "./tables.css";
import "../cards/cards.css";

function TableFooter(props) {
    if (!props.Text) {
        return null;
    }

    return (
        <div className="eone-ui-table-counter">{props.Text}</div>
    );
}

function TablePrompt(props) {
    const clickHandler = () => {
        if (props.OnClick) {
            if (props.Data) {
                props.OnClick(props.Data);
            } else {
                props.OnClick();
            }
        }
    }
    
    if (props.Large) {
        return (
            <button type="button" className="eone-ui-button eone-ui-button-tertiary" title={props.Title || props.Label} onClick={clickHandler}>
                {props.Icon && <FontAwesomeIcon icon={props.Icon} />}
                {props.Label}
            </button>
        )
    }

    //NOTE: FOR SC
    if (props.Medium) {
        return (
            <button type="button" className="eone-ui-button-medium eone-ui-button-tertiary" title={props.Title || props.Label} onClick={clickHandler}>
                {props.Icon && <FontAwesomeIcon icon={props.Icon} />}
                {props.Label}
            </button>
        )
    }

    if (props.Disabled) {
        return (
            <div className="eone-ui-button-small eone-ui-button-tertiary disabled" title={props.DisabledMessage || "This feature requires a subscription that your account does not have enabled"}>{props.Label}</div>
        );
    }

    return (
        <button type="button" className="eone-ui-button-small eone-ui-button-tertiary" title={props.Title || props.Label} onClick={clickHandler}>{props.Label}</button>
    );
}

function TablePrompts(props) {
    if (!props.PrimaryPrompts || props.PrimaryPrompts.length === 0) {
        return null;
    }

    const getPrimaryEmptyPrompts = () => {
        return (
            <div className="eone-ui-table-empty-primary-prompts-container">
                {props.PrimaryPrompts.map((prompt, index) => {
                    return (
                        <TablePrompt
                            key={index}
                            Icon={prompt.Icon}
                            Large={prompt.Large}
                            Label={prompt.Label}
                            Title={prompt.Title}
                            Data={prompt.Data}
                            Disabled={prompt.Disabled}
                            DisabledMessage={prompt.DisabledMessage}
                            OnClick={prompt.OnClick}
                            DataTestid={getDataTestId(props.DataTestId, prompt.DataTestId)} />
                    );
                })}
            </div>
        )
    }

    const getSecondaryEmptyPrompts = () => {
        if (!props.SecondaryPrompts || props.SecondaryPrompts.length === 0) {
            return null;
        }

        return (<>
            <div className="eone-ui-table-empty-prompt-separator">{"or"}</div>
            <div className="eone-ui-table-empty-secondary-prompts-container">
                {props.SecondaryPrompts.map((prompt, index) => {
                    return (
                        <TablePrompt
                            key={index}
                            Icon={prompt.Icon}
                            Label={prompt.Label}
                            Title={prompt.Title}
                            Data={prompt.Data}
                            Disabled={prompt.Disabled}
                            DisabledMessage={prompt.DisabledMessage}
                            OnClick={prompt.OnClick}
                            DataTestid={getDataTestId(props.DataTestId, prompt.DataTestId)} />
                    );
                })}
            </div>
        </>)
    }

    return (<>
        {getPrimaryEmptyPrompts()}
        {getSecondaryEmptyPrompts()}
    </>)
}

function TableContainerHeader(props) {
    const { containerRef, getOverflow } = useOverflowObserver(1);
    const compact = getOverflow(0);

    if (!props.Search && !props.ShowSelectTableColumns && !props.Tabs && !props.Heading && !props.HeaderButtons && !props.ShowRefresh && !props.AllowViewSwitching && !props.AllowDetailsPanel) {
        return <TableContainerHeaderEmpty />;
    }

    const isCardView = props.AllowViewSwitching && props.ViewMode === TableViewMode.Cards;

    const getSearchAndSelectColumns = () => {
        if (!props.Search && !props.ShowSelectTableColumns && !props.HeaderButtons && !props.ShowSelectTableRows && !props.ShowRefresh && !props.AllowViewSwitching && !props.AllowDetailsPanel) {
            return null;
        }

        return (
            <div className="eone-ui-table-container-header-search-and-buttons" ref={containerRef}>
                {getSearch()}
                <div className="eone-ui-table-container-header-buttons">
                    {getHeaderButtons()}
                    {getRowSelectAll()}
                    {getRowSelectNone()}
                    {isCardView ? getCardSortAndFilters() : getColumnSelect()}
                    {getRefresh()}
                    {getViewToggle()}
                    {getDetailsPanelToggle()}
                </div>
            </div>
        );
    };

    const getSearch = () => {
        const visible = props.Search === true;

        if (visible) {
            return (
                <InputString
                    Visible={visible}
                    Placeholder="Search"
                    Icon={faSearch}
                    Value={props.SearchValue}
                    OnChange={props.OnSearch}
                    DataTestId={getDataTestId(props.DataTestId, "search")} />
            );
        }

        return <div></div>;
    }

    const getHeaderButtons = () => {
        if (!props.HeaderButtons || props.HeaderButtons.length === 0) return null;
        return props.HeaderButtons.map((headerButton, index) => {
            return (
                <Button
                    key={index}
                    Class={"eone-ui-button-container-tertiary"}
                    Visible
                    Label={headerButton.Label}
                    HideLabel={compact}
                    Title={headerButton.Title || props.Label}
                    Icon={headerButton.Icon}
                    DataTestId={getDataTestId("header-button", headerButton.Name)}
                    OnClick={headerButton.OnClick} />
            );
        });
    };

    const getColumnSelect = () => {
        if (props.ShowSelectTableColumns !== true) return null;
        return (
            <SelectTableColumnsDropdown
                Class="eone-ui-button-container-tertiary"
                HideLabel={compact}
                Columns={props.Columns}
                SelectedColumns={props.SelectedColumns}
                OnSelectedColumnsChange={props.OnSelectedColumnsChange}
                DataTestId={getDataTestId(props.DataTestId, "select-table-columns")} />
        );
    };

    const getViewToggle = () => {
        if (!props.AllowViewSwitching) return null;
        const isGrid = props.ViewMode !== TableViewMode.Cards;
        const setGrid = () => props.OnViewModeChange?.(TableViewMode.Grid);
        const setCards = () => props.OnViewModeChange?.(TableViewMode.Cards);
        const gridClass = `eone-ui-button-container-tertiary eone-ui-table-view-toggle-button${isGrid ? " eone-ui-table-view-toggle-button-active" : ""}`;
        const cardsClass = `eone-ui-button-container-tertiary eone-ui-table-view-toggle-button${!isGrid ? " eone-ui-table-view-toggle-button-active" : ""}`;
        return (
            <div className="eone-ui-table-view-toggle" role="group" aria-label="View mode">
                <Button
                    Class={gridClass}
                    Label="Grid"
                    HideLabel={compact}
                    Icon={faList}
                    Title="Grid"
                    OnClick={setGrid}
                    DataTestId={getDataTestId(props.DataTestId, "view-toggle-grid")} />
                <Button
                    Class={cardsClass}
                    Label="Cards"
                    HideLabel={compact}
                    Icon={faTableCells}
                    Title="Cards"
                    OnClick={setCards}
                    DataTestId={getDataTestId(props.DataTestId, "view-toggle-cards")} />
            </div>
        );
    };

    const getDetailsPanelToggle = () => {
        if (!props.AllowDetailsPanel) return null;
        const isOpen = props.DetailsPanelOpen === true;
        const buttonClass = `eone-ui-button-container-tertiary eone-ui-table-details-panel-toggle-button${isOpen ? " eone-ui-table-details-panel-toggle-button-active" : ""}`;
        const toggle = () => props.OnDetailsPanelToggle?.(!isOpen);
        return (
            <Button
                Class={buttonClass}
                Icon={faSidebarFlip}
                Title={isOpen ? "Hide details" : "Show details"}
                OnClick={toggle}
                DataTestId={getDataTestId(props.DataTestId, "details-panel-toggle")} />
        );
    };

    const getCardSortAndFilters = () => {
        return (<>{getCardSort()}{getCardFilters()}</>);
    };

    const getCardSort = () => {
        const sortableColumns = (props.Columns || []).filter(column => column.Sortable);
        if (sortableColumns.length === 0) return null;
        const currentSortColumn = sortableColumns.find(column => column.FieldName === props.Sorting?.FieldName);
        const directionIcon = props.Sorting?.Direction === TableSortDirection.Descending ? faSortAmountDown : faSortAmountUp;
        const sortLabel = currentSortColumn ? `Sort: ${currentSortColumn.Label}` : "Sort";
        const menuItems = sortableColumns.map(column => ({
            Label: column.Label,
            OnClick: () => props.OnSortChange?.(column.FieldName, props.Sorting?.Direction === TableSortDirection.Descending ? TableSortDirection.Descending : TableSortDirection.Ascending),
            DataTestId: getDataTestId("card-sort-field", column.FieldName)
        }));
        const toggleDirection = () => {
            if (!currentSortColumn) return;
            const newDirection = props.Sorting?.Direction === TableSortDirection.Descending ? TableSortDirection.Ascending : TableSortDirection.Descending;
            props.OnSortChange?.(currentSortColumn.FieldName, newDirection);
        };
        return (
            <div className="eone-ui-table-view-toggle" role="group" aria-label="Sort">
                <Button
                    Class={"eone-ui-button-container-tertiary eone-ui-table-view-toggle-button"}
                    Label={sortLabel}
                    HideLabel={compact}
                    Icon={faSort}
                    MenuItems={menuItems}
                    DropDirection={ButtonMenuDropDirection.DownLeft}
                    HideDropDownIndicator={true}
                    DataTestId={getDataTestId("card-sort")} />
                {currentSortColumn && (
                    <Button
                        Class={"eone-ui-button-container-tertiary eone-ui-table-view-toggle-button"}
                        Title={props.Sorting?.Direction === TableSortDirection.Descending ? "Sort ascending" : "Sort descending"}
                        Icon={directionIcon}
                        OnClick={toggleDirection}
                        DataTestId={getDataTestId("card-sort-direction")} />
                )}
            </div>
        );
    };

    const getCardFilters = () => {
        const filterableColumns = (props.Columns || []).filter(column => column.Filterable);
        if (filterableColumns.length === 0) return null;
        let isFirst = true;
        const buttons = filterableColumns.map((column, index) => {
            const columnFilterValues = props.FilterValues?.[column.FieldName] || [];
            if (columnFilterValues.length <= 1) return null;
            const selected = columnFilterValues.filter(item => item.Selected);
            const label = selected.length === 0
                ? column.Label
                : selected.length === 1
                    ? `${column.Label}: ${selected[0].Value}`
                    : `${column.Label} (${selected.length})`;
            const menuItems = columnFilterValues.map(filterValue => ({
                Label: filterValue.Value?.toString() || "",
                Icon: filterValue.Selected ? faCheckSquare : faSquare,
                OnClick: () => {
                    const current = (props.RawFilterValues?.[column.FieldName] || []).slice();
                    const idx = current.indexOf(filterValue.Key);
                    if (idx >= 0) current.splice(idx, 1);
                    else current.push(filterValue.Key);
                    props.OnFilterChange?.(column.FieldName, current);
                },
                DataTestId: getDataTestId("card-filter-option", column.FieldName, filterValue.Key)
            }));
            const icon = (isFirst || compact) ? faFilter : undefined;
            isFirst = false;
            return (
                <Button
                    key={index}
                    Class={"eone-ui-button-container-tertiary eone-ui-table-view-toggle-button"}
                    Label={label}
                    HideLabel={compact}
                    Title={label}
                    Icon={icon}
                    MenuItems={menuItems}
                    DropDirection={ButtonMenuDropDirection.DownLeft}
                    HideDropDownIndicator={true}
                    DataTestId={getDataTestId("card-filter", column.FieldName)} />
            );
        });
        if (buttons.every(b => b === null)) return null;
        return (
            <div className="eone-ui-table-view-toggle" role="group" aria-label="Filters">
                {buttons}
            </div>
        );
    };

    const selectAllRows = () => {
        props.SelectTableRows(true);
    };

    const selectNoneRows = () => {
        props.SelectTableRows(false);
    };
    
    const getRowSelectAll = () => {
        const visible = props.ShowSelectTableRows === true;
        if (visible) {
            return (
                <Button
                    Class={"eone-ui-button-container-tertiary"}
                    Visible={visible}
                    Label={"Select all"}
                    HideLabel={compact}
                    Icon={faCheckSquare}
                    DataTestId={getDataTestId("select-all-table-rows")}
                    OnClick={selectAllRows} />
            );
        }
        return null;
    };

    const getRowSelectNone = () => {
        const visible = props.ShowSelectTableRows === true;
        if (visible) {
            return (
                <Button
                    Class={"eone-ui-button-container-tertiary"}
                    Visible={visible}
                    Label={"Select none"}
                    HideLabel={compact}
                    Icon={faSquare}
                    DataTestId={getDataTestId("select-none-table-rows")}
                    OnClick={selectNoneRows} />
            );
        }
        return null;
    };

    const getRefresh = () => {
        const visible = props.ShowRefresh === true;

        if (visible) {
            return (
                <Button
                    Class={"eone-ui-button-container-tertiary"}
                    Visible={visible}
                    Label={"Refresh"}
                    HideLabel={compact}
                    Icon={faRefresh}
                    DataTestId={getDataTestId("refresh")}
                    OnClick={props.Refresh} />
            );
        }

        return null;
    };

    const getTabs = () => {
        if (props.Tabs?.length > 0) {
            return (
                <ButtonToggle
                    Visible={true}
                    Options={props.Tabs}
                    Alignment={props.TabAlignment}
                    Value={props.ActiveTab}
                    OnChange={props.ChangeTab}
                    DataTestId={props.TabDataTestId} />
            );
        }

        return null;
    };

    const getHeading = () => {
        if (props.Heading) {
            return (
                <div className="eone-ui-table-container-header-heading-container">
                    <div className={`eone-ui-table-container-header-heading ${props.HeadingClass}`}>{props.Heading}</div>
                </div>
            );
        }

        return null;
    };

    if (props.Tabs?.length > 0) {
        const toolbarHasContent = props.Heading || props.Search || props.ShowSelectTableColumns || props.HeaderButtons || props.ShowRefresh || props.AllowViewSwitching || props.AllowDetailsPanel;
        return (
            <div className="eone-ui-table-container-header-stacked">
                <div className="eone-ui-table-container-header-tabs-row">
                    {getTabs()}
                </div>
                {toolbarHasContent ? (
                    <div className="eone-ui-table-container-header">
                        {getHeading()}
                        {getSearchAndSelectColumns()}
                    </div>
                ) : null}
            </div>
        );
    }

    return (
        <div className="eone-ui-table-container-header">
            {getHeading()}
            {getSearchAndSelectColumns()}
        </div>
    );
}

function TableContainerHeaderEmpty() {
    return (
        <div className="eone-ui-table-container-header-empty">
        </div>
    );
}

function TableContainerFooterForm(props) {
    if (!props.FooterForm) {
        return null;
    }

    return (
        <div className="eone-ui-table-footer-form">
            {props.FooterForm}
        </div>
    );
}

function TableContainerHeaderForm(props) {
    if (!props.HeaderForm) {
        return null;
    }

    return (
        <div className="eone-ui-table-header-form">
            {props.HeaderForm}
        </div>
    );
}

function TableContainer(props) {
    const getContainerClass = () => {
        let containerClass = "eone-ui-table-container";
        if (props.BodyHeight) {
            containerClass = "eone-ui-table-container-with-height";
        }
        if (props.HorizontalScroll) {
            containerClass += " eone-ui-table-container-horizontal-scroll";
        }
        return containerClass;
    };

    const headerForm = <TableContainerHeaderForm HeaderForm={props.HeaderForm}/>;
    const header = (
        <TableContainerHeader
            Heading={props.Heading}
            HeadingClass={props.HeaderClass}
            HeaderButtons={props.HeaderButtons}
            Search={props.Search}
            OnSearch={props.OnSearch}
            SearchValue={props.SearchValue}
            ShowSelectTableColumns={props.ShowSelectTableColumns}
            SelectedColumns={props.SelectedColumns}
            OnSelectedColumnsChange={props.OnSelectedColumnsChange}
            ShowSelectTableRows={props.ShowSelectTableRows}
            SelectTableRows={props.SelectTableRows}
            ShowRefresh={props.ShowRefresh}
            Refresh={props.Refresh}
            Tabs={props.Tabs}
            TabAlignment={props.TabAlignment}
            ActiveTab={props.ActiveTab}
            ChangeTab={props.ChangeTab}
            TabDataTestId={props.TabDataTestId}
            AllowViewSwitching={props.AllowViewSwitching}
            ViewMode={props.ViewMode}
            OnViewModeChange={props.OnViewModeChange}
            AllowDetailsPanel={props.AllowDetailsPanel}
            DetailsPanelOpen={props.DetailsPanelOpen}
            OnDetailsPanelToggle={props.OnDetailsPanelToggle}
            Columns={props.Columns}
            Sorting={props.Sorting}
            OnSortChange={props.OnSortChange}
            FilterValues={props.FilterValues}
            RawFilterValues={props.RawFilterValues}
            OnFilterChange={props.OnFilterChange}
            DataTestId={props.DataTestId} />
    );
    const headerFormBelowTabs = props.HeaderFormBelowTabs && props.Tabs?.length > 0;

    return (
        <div className={props.ContainerClass}>
            {headerFormBelowTabs ? header : headerForm}
            {headerFormBelowTabs ? headerForm : header}
            <div className={getContainerClass()} >
                {props.children}
            </div>
            <TableFooter Text={props.FooterText} />
            <TableContainerFooterForm FooterForm={props.FooterForm}/>
        </div>
    );
}

function Table(props) {
    const [editing, setEditing] = useState(false);
    const [rowSorting, setRowSorting] = useState([]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    );

    const effectiveOnColumnResize = props.TableState?.OnColumnResize || props.OnColumnResize;

    const {
        tableRef,
        getColumnWidth,
        startResize,
        autoFitColumn,
        resetWidths,
    } = useColumnResize({
        Columns: props.Columns,
        SelectedColumns: props.TableState?.Columns,
        OnColumnResize: effectiveOnColumnResize,
        InitialColumnWidths: props.TableState?.ColumnWidths,
    });

    const prevSelectedColumnsRef = useRef(props.TableState?.Columns);
    useEffect(() => {
        if (prevSelectedColumnsRef.current !== props.TableState?.Columns) {
            const persistedWidths = props.TableState?.ColumnWidths || {};
            const newColumns = props.TableState?.Columns || [];
            if (newColumns.length > 0 && Object.keys(persistedWidths).length > 0) {
                const retainedWidths = {};
                for (const col of newColumns) {
                    if (persistedWidths[col] !== undefined) {
                        retainedWidths[col] = persistedWidths[col];
                    }
                }
                resetWidths(retainedWidths);
            } else {
                resetWidths();
            }
            prevSelectedColumnsRef.current = props.TableState?.Columns;
        }
    }, [props.TableState?.Columns, props.TableState?.ColumnWidths, resetWidths]);

    useEffect(() => {
        if ((!editing && !props.Reorderable)) {
            generateRows();
        }
    }, [props.TableState, props.Rows, props.Columns, props.IdField]);

    useEffect(() => {
        if (!editing && !props.Reorderable) generateRows();
    }, [editing]);

    const changeHandler = (id, fieldName, value) => {
        if (typeof props.ChangeValue === "function") {
            setEditing(true);
            props.ChangeValue(id, fieldName, value);
        }
    };

    const updateHandler = (id, fieldName, value) => {
        if (typeof props.UpdateValue === "function") {
            props.UpdateValue(id, fieldName, value);
            setEditing(false);
        }
    };

    const selectRow = (idField) => {
        if (typeof props.SelectRow === "function") {
            props.SelectRow(idField);
        }
    };

    const handleDragEnd = (event) => {
        if (event.active.id !== event.over.id) {
            const startIndex = props.Rows.findIndex(row => row[props.IdField] === event.active.id);
            const endIndex = props.Rows.findIndex(row => row[props.IdField] === event.over.id);
            props.Reorder(event.active.id, startIndex, endIndex);
        }
    };

    const generateRows = () => {
        let rows = searchRows();
        rows = filterRows(rows);
        sortRows(rows);
    };

    const isSearched = (row, column) => {
        if (column.FieldName in row && row[column.FieldName]) {
            // todo - handle selects
            // todo - handle integers (should only be equal)
            if (column.FieldType === TableFieldType.Tags) {
                const tags = Array.isArray(row[column.FieldName]) ? row[column.FieldName] : [];
                const search = props.TableState.Search.toLowerCase();
                return tags.some(tag => tag.Name && tag.Name.toLowerCase().includes(search));
            }
            return row[column.FieldName].toLowerCase().includes(props.TableState.Search.toLowerCase());
        }
        return false;
    };

    const isRowSearched = (row, columns) => {
        for (let column = 0, numberOfColumns = columns.length; column < numberOfColumns; column++) {
            if (isSearched(row, columns[column])) {
                return true;
            }
        }
        return false;
    };

    const searchRows = () => {
        if (!props.TableState?.Search) return props.Rows;
        const searchableColumns = props.Columns.filter(column => column.Searchable);
        if (searchableColumns.length === 0) return props.Rows;
        let rows = [];
        for (let row = 0, numberOfRows = props.Rows.length; row < numberOfRows; row++) {
            if (isRowSearched(props.Rows[row], searchableColumns)) {
                rows.push(props.Rows[row]);
            }
        }
        return rows;
    };

    const isFiltered = (row, fieldName) => {
        const selected = props.TableState?.FilterValues[fieldName];
        if (!selected || selected.length === 0) return false;
        const column = props.Columns.find(c => c.FieldName === fieldName);
        if (column?.FieldType === TableFieldType.Tags) {
            const rowTags = Array.isArray(row[fieldName]) ? row[fieldName] : [];
            return selected.some(tagId => rowTags.some(t => t.TagId === tagId));
        }
        if (fieldName in row && row[fieldName] !== undefined) {
            return selected.includes(row[fieldName]);
        }
        return false;
    };

    const isRowFiltered = (row) => {
        const fieldNames = Object.getOwnPropertyNames(props.TableState.FilterValues);
        if (fieldNames.length === 0) return true;

        let filtered = true;
        for (let i = 0, len = fieldNames.length; i < len; i++) {
            if (props.TableState.FilterValues[fieldNames[i]] && props.TableState.FilterValues[fieldNames[i]].length > 0 && !isFiltered(row, fieldNames[i])) {
                filtered = false;
            }
        }
        return filtered;
    };

    const filterRows = (rows) => {
        if (!props.TableState?.FilterValues) return rows;
        let filterRows = [];
        for (let row = 0, numberOfRows = rows.length; row < numberOfRows; row++) {
            if (isRowFiltered(rows[row])) {
                filterRows.push(rows[row]);
            }
        }
        return filterRows;
    };

    const getSelectDisplayValue = (row, column) => {
        const value = row[column.FieldName];
        if (!value) return "";
        if (!column.Options) return value;
        const options = column.Options || row[column.RowOptions] || [];
        const option = options.find(item => item.Key === value);
        if (!option) return value?.toString().toLowerCase();
        if (!option.Value) return value?.toString().toLowerCase();
        return option.Value?.toString().toLowerCase();
    };

    const sortRows = (rows) => {
        const collator = new Intl.Collator(props.Locale, { caseFirst: 'upper' });
        if (!props.TableState?.Sort || !props.TableState?.Sort.FieldName) {
            setSorting(rows);
            return;
        }
        const sortColumn = props.Columns.find(column => column.FieldName === props.TableState.Sort.FieldName);
        if (!sortColumn) {
            setSorting(rows);
            return;
        }
        if (typeof props.CustomSort === 'function') {
            const sorted = props.CustomSort(rows, props.TableState.Sort.FieldName, props.TableState.Sort.Direction, sortColumn);
            setSorting(sorted);
            return;
        }
        switch (sortColumn.FieldType) {
            case TableFieldType.Select:
                if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
                    rows.sort((a, b) => { return -collator.compare(getSelectDisplayValue(a, sortColumn), getSelectDisplayValue(b, sortColumn)); });
                } else {
                    rows.sort((a, b) => { return collator.compare(getSelectDisplayValue(a, sortColumn), getSelectDisplayValue(b, sortColumn)); });
                }
                break;
            case TableFieldType.Checkbox:
                if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
                    rows.sort((a, b) => { return Boolean(a[sortColumn.FieldName]) < Boolean(b[sortColumn.FieldName]) ? 1 : -1; });
                } else {
                    rows.sort((a, b) => { return Boolean(a[sortColumn.FieldName]) > Boolean(b[sortColumn.FieldName]) ? 1 : -1; });
                }
                break;
            case TableFieldType.Integer:
                if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
                    rows.sort((a, b) => { return Number.parseInt(a[sortColumn.FieldName]) < Number.parseInt(b[sortColumn.FieldName]) ? 1 : -1; });
                } else {
                    rows.sort((a, b) => { return Number.parseInt(a[sortColumn.FieldName]) > Number.parseInt(b[sortColumn.FieldName]) ? 1 : -1; });
                }
                break;
            case TableFieldType.Date:
            case TableFieldType.DateTime:
                if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
                    rows.sort((a, b) => {
                        const valueA = a?.[sortColumn.FieldName];
                        const valueB = b?.[sortColumn.FieldName];
                        // Empty dates always go to the end
                        if (!valueA && !valueB) return 0;
                        if (!valueA) return 1;
                        if (!valueB) return -1;
                        return new Date(valueB).getTime() - new Date(valueA).getTime();
                    });
                } else {
                    rows.sort((a, b) => {
                        const valueA = a?.[sortColumn.FieldName];
                        const valueB = b?.[sortColumn.FieldName];
                        // Empty dates always go to the end
                        if (!valueA && !valueB) return 0;
                        if (!valueA) return 1;
                        if (!valueB) return -1;
                        return new Date(valueA).getTime() - new Date(valueB).getTime();
                    });
                }
                break;
            default:
                if (props.TableState.Sort.Direction === TableSortDirection.Descending) {
                    rows.sort((a, b) => { return -1 * collator.compare(a[sortColumn.FieldName], b[sortColumn.FieldName]); });
                } else {
                    rows.sort((a, b) => { return collator.compare(a[sortColumn.FieldName], b[sortColumn.FieldName]); });
                }
                break;
        }
        setSorting(rows);
    };

    const setSorting = (rows) => {
        let newRowSorting = [];
        for (let i = 0, length = rows.length; i < length; i++) {
            newRowSorting.push(rows[i][props.IdField]);
        }
        setRowSorting(newRowSorting);
    };

    const getRows = (reorderable, buttonWidth) => {
        let rows = [];
        if (props.Reorderable || rowSorting.length === 0) {
            rows = props.Rows;
        } else {
            for (let i = 0, len = rowSorting.length; i < len; i++) {
                const row = props.Rows.find(item => item[props.IdField] === rowSorting[i]);
                if (row) rows.push(row);
            }
        }
        return rows.map((row, index) => {
            return (
                <TableRow
                    key={index}
                    Row={row}
                    Columns={props.Columns}
                    SelectedColumns={props.TableState?.Columns}
                    SelectRow={selectRow}
                    OnDoubleClick={props.OnRowDoubleClick}
                    Selected={props.IdField && !isNullOrUndefined(props.SelectedRowId) && row[props.IdField] === props.SelectedRowId}
                    Buttons={props.Buttons}
                    CollapseHiddenButtons={props.CollapseHiddenButtons}
                    IdField={props.IdField}
                    Locale={props.Locale}
                    Reorderable={reorderable}
                    Reorder={props.Reorder}
                    ChangeValue={changeHandler}
                    UpdateValue={updateHandler}
                    ButtonWidth={buttonWidth + 12}
                    FixedRowButtons={props.FixedRowButtons}
                    GetColumnWidth={getColumnWidth}
                    RowIcons={props.RowIcons}
                    OnFocus={props.OnFocus}
                    DataTestId={props.DataTestId} />
            );
        });
    };

    const getFieldLabel = (fieldName) => {
        const field = props.Columns.find(item => item.FieldName === fieldName);
        if (field) return field.Label;
        return fieldName;
    };

    const getFieldType = (fieldName) => {
        const field = props.Columns.find(item => item.FieldName === fieldName);
        if (field) return field.FieldType;
        return TableFieldType.String;
    };

    const getSelectLabel = (fieldName, value) => {
        const field = props.Columns.find(item => item.FieldName === fieldName);
        if (field) {
            const label = field.Options.find(item => item.Key === value)?.Value;
            if (label) return label;
        }
        return value;
    };

    const getFilterValueDescription = (filterField, valueNumber, fieldType) => {
        const value = props.TableState?.FilterValues[filterField][valueNumber];
        switch (fieldType) {
            case TableFieldType.Integer:
                return value;
            case TableFieldType.Date:
                return `'${value}'`; // todo - format based on locale
            case TableFieldType.Checkbox:
                return value ? "'Yes'" : "'No'";
            case TableFieldType.Select:
                return `'${getSelectLabel(filterField, value)}'`;
            default:
                return `'${value}'`;
        }
    };

    const getFilterDescription = (filterField) => {
        let description = getFieldLabel(filterField);
        const fieldType = getFieldType(filterField);
        if (props.TableState?.FilterValues[filterField].length === 1) {
            description = description + " equals " + getFilterValueDescription(filterField, 0, fieldType);
        } else {
            let filterValues = [];
            for (let i = 0, len = props.TableState?.FilterValues[filterField].length; i < len; i++) {
                filterValues.push(getFilterValueDescription(filterField, i, fieldType));
            }
            description = description + " is one of (" + filterValues.join(", ") + ")";
        }
        return description;
    };

    const getSearchMessage = () => {
        if (!props.TableState) return "";
        let message = "No records found for ";
        if (props.TableState?.Search) {
            message = `${message}'${props.TableState?.Search}'`
        }
        const filterFields = Object.getOwnPropertyNames(props.TableState?.FilterValues);
        if (filterFields.length > 0) {
            if (props.TableState?.Search) {
                message = `${message} and `;
            }
            let filterDescriptions = [];
            for (let i = 0, len = filterFields.length; i < len; i++) {
                filterDescriptions.push(getFilterDescription(filterFields[i]));
            }
            message = message + " " + filterDescriptions.join(" and ");
        }
        return message;
    };

    const clearFilters = () => {
        props.TableState.SetSearch("");
        props.TableState.ClearFilterValues();
    };

    const getFilterValues = () => {
        let filterValues = {};
        for (let i = 0, len = props.Columns.length; i < len; i++) {
            // only get filter values for selected columns
            if (props.Columns[i].Filterable && (!props.TableState?.Columns || props.TableState.Columns.length === 0 || props.Columns[i].Locked || props.TableState.Columns.includes(props.Columns[i].FieldName))) {
                let columnFilterValues = getColumnFilterValues(props.Columns[i]);
                columnFilterValues.sort((a, b) => { return a.Value?.toLowerCase() > b.Value?.toLowerCase() ? 1 : -1; });
                filterValues[props.Columns[i].FieldName] = columnFilterValues;
            }
        }
        return filterValues;
    };

    const isColumnFilterValueSelected = (fieldName, value) => {
        if (!props.TableState?.FilterValues) return false;
        if (!(fieldName in props.TableState.FilterValues)) return false;
        if (!props.TableState.FilterValues[fieldName]) return false;
        return props.TableState.FilterValues[fieldName].includes(value);
    };

    const getColumnFilterValues = (column) => {
        let filterValues = [];
        switch (column.FieldType) {
            case TableFieldType.Select:
                filterValues = getColumnFilterSelectValues(column);
                break;
            case TableFieldType.Checkbox:
                filterValues = [
                    { Key: false, Value: "No" },
                    { Key: true, Value: "Yes" }
                ];
                break;
            case TableFieldType.Date:
                // todo
                break;
            case TableFieldType.Integer:
                // todo
                break;
            case TableFieldType.Tags:
                filterValues = getColumnFilterTagValues(column);
                break;
            default:
                filterValues = getColumnFilterStringValues(column);
                break;
        }

        return filterValues.map((filterValue) => {
            return {
                Key: filterValue.Key,
                Value: filterValue.Value,
                Selected: isColumnFilterValueSelected(column.FieldName, filterValue.Key)
            };
        });
    };

    const getColumnFilterSelectValues = (column) => {
        // todo - remove unused values
        // todo - add none if blank values exist
        // todo - sort options
        let options = column.Options.slice(0);
        options = options.filter(option => option.Key?.toString().substring(0, 1) !== "-"); // remove separators

        return options;
    };

    const getColumnFilterStringValues = (column) => {
        let filterValues = [];
        let includeNone = false;
        for (let i = 0, len = props.Rows.length; i < len; i++) {
            const value = props.Rows[i][column.FieldName];
            if (value) {
                if (!filterValues.includes(value)) {
                    filterValues.push(value);
                }
            } else {
                includeNone = true;
            }
        }
        if (includeNone) {
            // todo
        }
        return filterValues.map((filterValue) => {
            return {
                Key: filterValue,
                Value: filterValue
            };
        });
    };

    const getColumnFilterTagValues = (column) => {
        const seen = new Map();
        for (let i = 0, len = props.Rows.length; i < len; i++) {
            const tags = props.Rows[i][column.FieldName];
            if (!Array.isArray(tags)) continue;
            for (let j = 0, jl = tags.length; j < jl; j++) {
                const tag = tags[j];
                if (tag && tag.TagId && !seen.has(tag.TagId)) {
                    seen.set(tag.TagId, tag.Name);
                }
            }
        }
        return Array.from(seen.entries())
            .map(([tagId, name]) => ({ Key: tagId, Value: name }))
            .sort((a, b) => (a.Value ?? "").localeCompare(b.Value ?? ""));
    };

    const getFooterText = (filteredRowCount, rowCount) => {
        if (props.FooterText) return props.FooterText;
        if (props.ShowRecordCount) {
            if (filteredRowCount !== rowCount) {
                if (rowCount === 1) {
                    return `${filteredRowCount} of ${rowCount} ${props.ItemName}`;
                } else {
                    return `${filteredRowCount} of ${rowCount} ${props.ItemPlural}`;
                }
            } else {
                if (filteredRowCount === 1) {
                    return `${filteredRowCount} ${props.ItemName}`;
                } else {
                    return `${filteredRowCount} ${props.ItemPlural}`;
                }
            }
        }
        return "";
    };

    const getTableClass = () => {
        let tableClass = "eone-ui-table";
        if (props.HorizontalScroll) {
            tableClass += " eone-ui-table-horizontal-scroll";
        }
        return tableClass;
    };

    const isCardView = props.AllowViewSwitching && props.TableState?.ViewMode === TableViewMode.Cards;

    const getCards = (reorderable) => {
        let rows = [];
        if (props.Reorderable || rowSorting.length === 0) {
            rows = props.Rows;
        } else {
            for (let i = 0, len = rowSorting.length; i < len; i++) {
                const row = props.Rows.find(item => item[props.IdField] === rowSorting[i]);
                if (row) rows.push(row);
            }
        }
        const gridClass = props.CardColumns ? "eone-ui-card-grid eone-ui-card-grid-fixed" : "eone-ui-card-grid";
        const gridStyle = props.CardColumns ? { "--pd-card-cols": props.CardColumns } : undefined;
        return (
            <div className={gridClass} style={gridStyle}>
                {rows.map((row, index) => (
                    <TableCard
                        key={index}
                        Row={row}
                        Columns={props.Columns}
                        IdField={props.IdField}
                        SelectedRowId={props.SelectedRowId}
                        OnSelect={props.SelectRow}
                        OnDoubleClick={props.OnRowDoubleClick}
                        RowButtons={props.Buttons}
                        CardIconType={props.CardIconType}
                        CardIconInitialsColumn={props.CardIconInitialsColumn}
                        CardIconAvatarColumn={props.CardIconAvatarColumn}
                        Locale={props.Locale}
                        Reorderable={reorderable}
                        DataTestId={props.DataTestId} />
                ))}
            </div>
        );
    };

    const hasLockedColumn = Array.isArray(props.Columns) && props.Columns.some(column => column.Locked && column.Visible !== false);
    const noColumnsSelected = !hasLockedColumn
        && Array.isArray(props.TableState?.Columns)
        && props.TableState.Columns.length > 0
        && props.TableState.Columns.every(fieldName => fieldName === SELECT_COLUMNS_NONE_SENTINEL);

    const renderNoColumnsSelected = () => (
        <div className="eone-ui-table-no-columns-selected" data-testid={getDataTestId(props.DataTestId, "no-columns-selected")}>
            No columns are selected
        </div>
    );

    const getTableContents = () => {
        if (!props.Reorderable && rowSorting.length === 0) {
            return (
                <div className="eone-ui-table-empty" >
                    <label className="eone-ui-table-empty-prompt">{getSearchMessage()}</label>
                    <div className="eone-ui-table-empty-link-container">
                        <button type="button" className="eone-ui-button-small eone-ui-button-secondary" data-testid={getDataTestId(props.DataTestId, "clear-all-filters")} onClick={clearFilters}>Clear all filters</button>
                    </div>
                </div>
            );
        }

        if (isCardView) {
            return getCards(false);
        }

        if (noColumnsSelected) {
            return renderNoColumnsSelected();
        }

        return (
            <table className={getTableClass()} ref={tableRef}>
                <TableHeaderRow
                    Visible={!props.HideTableHeader}
                    Columns={props.Columns}
                    Sticky={props.StickyHeader}
                    Buttons={props.Buttons}
                    SelectedColumns={props.TableState?.Columns}
                    Filter={props.TableState?.SetFilterValues}
                    FilterValues={getFilterValues()}
                    Sorting={props.TableState?.Sort}
                    Sort={props.TableState?.SetSort}
                    ButtonWidth={buttonWidth}
                    GetColumnWidth={getColumnWidth}
                    OnResizeStart={startResize}
                    OnAutoFit={autoFitColumn}
                    DataTestId={props.DataTestId} />
                <tbody>
                    {getRows(reorderable, buttonWidth)}
                </tbody>
            </table>
        );
    };

    const containerClass = `eone-ui-table-master-container ${props.MaxHeightWithBreadcrumb ? "eone-ui-table-max-height-with-breadcrumb" : ""}`;

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    if (props.Loading) {
        return (
            <div className={`eone-ui-table-loading ${containerClass}`} style={{height: props.BodyHeight}}>
                <div>
                    <FontAwesomeIcon icon={faSpinnerThird} spin />
                    <label className="eone-ui-table-loading-prompt" data-testid={getDataTestId(props.DataTestId, "loading")}>{props.LoadingMessage || "Loading..."}</label>
                </div>
            </div>
        );
    }

    if (props.Rows.length === 0) {
        return (
            <div className={containerClass} style={{height: props.BodyHeight}}>
                <TableContainerHeaderForm HeaderForm={props.HeaderForm}/>
                <TableContainerHeader
                    HeadingClass={props.HeadingClass}
                    Heading={props.Heading}
                    HeaderButtons={props.HeaderButtons}
                    Tabs={props.Tabs}
                    TabAlignment={props.TabAlignment}
                    ActiveTab={props.ActiveTab}
                    ChangeTab={props.ChangeTab}
                    TabDataTestId={props.TabDataTestId} />
                <div className={"eone-ui-table-empty"}> {/*props.HideTableHeader ? "eone-ui-table-empty-no-header" :*/}
                    <div className={"eone-ui-table-empty-prompt-container"}>
                        {props.EmptyIcon && <FontAwesomeIcon className="eone-ui-table-empty-icon" icon={props.EmptyIcon} />}
                        <div className="eone-ui-table-empty-title">{props.EmptyTitle}</div>
                        <div className="eone-ui-table-empty-prompt">{props.EmptyMessage}</div>
                    </div>
                    <div className="eone-ui-table-empty-link-container">
                        <TablePrompts
                            PrimaryPrompts={props.PrimaryEmptyPrompts ?? props.EmptyPrompts}
                            SecondaryPrompts={props.SecondaryEmptyPrompts}
                            DataTestId={props.DataTestId} />
                    </div>
                </div>
                <TableContainerFooterForm FooterForm={props.FooterForm}/>
            </div>
        );
    }

    const reorderable = props.Reorderable && props.Rows.length > 1;

    const footerText = getFooterText(rowSorting.length, props.Rows.length);
    const buttonWidth = props.Buttons && props.Buttons.length > 0 ? 44 + (props.Buttons.length - 1) * 43 : 0;

    if (reorderable) {
        const modifiers = [restrictToVerticalAxis, restrictToParentElement];
        const itemIds = props.Rows.map(row => row[props.IdField]);
        return (
            <TableContainer
                Heading={props.Heading}
                HeadingClass={props.HeadingClass}
                HeaderButtons={props.HeaderButtons}
                BodyHeight={props.BodyHeight}
                HorizontalScroll={props.HorizontalScroll}
                FooterText={footerText}
                Search={props.Search}
                OnSearch={props.OnSearch}
                SearchValue={props.SearchValue}
                ShowSelectTableColumns={props.ShowSelectTableColumns && !isCardView}
                SelectedColumns={props.TableState?.Columns}
                OnSelectedColumnsChange={props.TableState?.SetColumns}
                ShowRefresh={props.ShowRefresh}
                Refresh={props.Refresh}
                Tabs={props.Tabs}
                TabAlignment={props.TabAlignment}
                ActiveTab={props.ActiveTab}
                ChangeTab={props.ChangeTab}
                TabDataTestId={props.TabDataTestId}
                ContainerClass={containerClass}
                HeaderForm={props.HeaderForm}
                HeaderFormBelowTabs={props.HeaderFormBelowTabs}
                FooterForm={props.FooterForm}
                AllowViewSwitching={props.AllowViewSwitching}
                ViewMode={props.TableState?.ViewMode}
                OnViewModeChange={props.TableState?.SetViewMode}
                AllowDetailsPanel={props.AllowDetailsPanel}
                DetailsPanelOpen={props.DetailsPanelOpen}
                OnDetailsPanelToggle={props.OnDetailsPanelToggle}
                Columns={props.Columns}
                Sorting={props.TableState?.Sort}
                OnSortChange={props.TableState?.SetSort}
                FilterValues={getFilterValues()}
                RawFilterValues={props.TableState?.FilterValues}
                OnFilterChange={props.TableState?.SetFilterValues}
                DataTestId={props.DataTestId}>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={modifiers}>
                    <SortableContext items={itemIds}>
                        {isCardView ? (
                            getCards(true)
                        ) : noColumnsSelected ? (
                            renderNoColumnsSelected()
                        ) : (
                            <table className={getTableClass()} ref={tableRef}>
                                <TableHeaderRow
                                    Visible={!props.HideTableHeader}
                                    Reorderable
                                    Sticky={props.StickyHeader}
                                    Columns={props.Columns}
                                    SelectedColumns={props.TableState?.Columns}
                                    FilterValues={getFilterValues()}
                                    Buttons={props.Buttons}
                                    Sorting={props.TableState?.Sort}
                                    Sort={props.TableState?.SetSort}
                                    ButtonWidth={buttonWidth}
                                    GetColumnWidth={getColumnWidth}
                                    OnResizeStart={startResize}
                                    OnAutoFit={autoFitColumn}
                                    DataTestId={props.DataTestId} />
                                <tbody>
                                    {getRows(reorderable, buttonWidth)}
                                </tbody>
                            </table>
                        )}
                    </SortableContext>
                </DndContext>

            </TableContainer>
        );
    }

    return (
        <TableContainer
            Heading={props.Heading}
            HeadingClass={props.HeadingClass}
            HeaderButtons={props.HeaderButtons}
            BodyHeight={props.BodyHeight}
            HorizontalScroll={props.HorizontalScroll}
            FooterText={footerText}
            Search={props.Search}
            OnSearch={props.OnSearch}
            SearchValue={props.SearchValue}
            ShowSelectTableColumns={props.ShowSelectTableColumns && !isCardView}
            SelectedColumns={props.TableState?.Columns}
            OnSelectedColumnsChange={props.TableState?.SetColumns}
            ShowSelectTableRows={props.ShowSelectTableRows}
            SelectTableRows={props.SelectTableRows}
            ShowRefresh={props.ShowRefresh}
            Refresh={props.Refresh}
            Tabs={props.Tabs}
            TabAlignment={props.TabAlignment}
            ActiveTab={props.ActiveTab}
            ChangeTab={props.ChangeTab}
            TabDataTestId={props.TabDataTestId}
            ContainerClass={containerClass}
            HeaderForm={props.HeaderForm}
            HeaderFormBelowTabs={props.HeaderFormBelowTabs}
            FooterForm={props.FooterForm}
            AllowViewSwitching={props.AllowViewSwitching}
            ViewMode={props.TableState?.ViewMode}
            OnViewModeChange={props.TableState?.SetViewMode}
            AllowDetailsPanel={props.AllowDetailsPanel}
            DetailsPanelOpen={props.DetailsPanelOpen}
            OnDetailsPanelToggle={props.OnDetailsPanelToggle}
            Columns={props.Columns}
            Sorting={props.TableState?.Sort}
            OnSortChange={props.TableState?.SetSort}
            FilterValues={getFilterValues()}
            RawFilterValues={props.TableState?.FilterValues}
            OnFilterChange={props.TableState?.SetFilterValues}
            DataTestId={props.DataTestId}>

            {getTableContents()}

        </TableContainer>
    );
}

export { Table, TablePrompts };
